import { Box, Divider, Flex, Text } from '@chakra-ui/react'
import { CervicalConsistency, CervicalPosition, MembraneStatus } from '@hb/shared'
import { format } from 'date-fns'
import { useMemo, type ComponentProps, type FC, type HTMLAttributes, type MouseEvent } from 'react'
import {
  CartesianGrid,
  ComposedChart,
  Line,
  Tooltip as RechartsTooltip,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
  type LabelProps,
} from 'recharts'

import { ChartLegend, type ChartLegendItem } from './ChartLegend'

// ── Constants ──────────────────────────────────────────────────────────────

/** 1 cm per hour expressed as cm per millisecond */
const CM_PER_MS = 1 / (60 * 60 * 1000)

/** Action line starts 4 hours after alert line (WHO partogram standard) */
const ACTION_OFFSET_MS = 4 * 60 * 60 * 1000

/** Sample alert/action lines every 15 minutes */
const LINE_STEP_MS = 15 * 60 * 1000

/** Max characters for narrative markers on the chart; full text on hover via Chakra Tooltip */
const NARRATIVE_LABEL_DISPLAY_MAX = 14

const NARRATIVE_LABEL_FOREIGN_OBJECT_W = 140
const NARRATIVE_LABEL_FOREIGN_OBJECT_H = 20

/** Default top margin when no narrative markers sit above the plot */
const PARTOGRAM_MARGIN_TOP_DEFAULT = 12
/**
 * With narrative markers, the label foreignObject top is ~22px above the plot (`foY = labelY − 17`);
 * margin.top must be large enough or the SVG clips the label.
 */
const PARTOGRAM_MARGIN_TOP_WITH_NARRATIVES = 32

// ── Data Shapes ────────────────────────────────────────────────────────────

/**
 * A single point on the partogram x-axis. May carry cervical exam data, a
 * narrative note, or both.
 */
export interface PartogramDataPoint {
  time: number
  dilation: number | null
  station: number | null
  /** Cervical effacement percentage (0–100) — cervical exam points only */
  effacement?: number
  /** Cervical position — cervical exam points only */
  position?: CervicalPosition
  /** Cervical consistency — cervical exam points only */
  consistency?: CervicalConsistency
  /** Membrane status — cervical exam points only */
  membranes?: MembraneStatus
  /** Presenting part (e.g. "Vertex") — cervical exam points only */
  presentingPart?: string
  /** Full note body — present only for narrative-entry points */
  noteBody?: string
  /**
   * Non-null sentinel (value: 1) on narrative-only points.
   * Paired with a hidden Line so recharts never filters the entire payload
   * array for these points — giving the tooltip access to `noteBody` even
   * when dilation and station are both null.
   */
  _snap?: 1
  /** Firestore document ID — used to call selectEntry on click */
  entryId?: string
}

interface AlertPoint {
  time: number
  alertDilation: number
}

interface ActionPoint {
  time: number
  actionDilation: number
}

export interface ProcedureOverlay {
  entryId: string
  name: string
  startTime: number
  endTime: number | undefined
}

export interface NarrativeMarker {
  time: number
  label: string
}

// ── Alert / Action Line Generators ─────────────────────────────────────────

function buildAlertLine(activeStart: number, maxTime: number): AlertPoint[] {
  const pts: AlertPoint[] = []
  for (let t = activeStart; t <= maxTime; t += LINE_STEP_MS) {
    const d = 4 + CM_PER_MS * (t - activeStart)
    if (d >= 10) {
      pts.push({ time: activeStart + 6 / CM_PER_MS, alertDilation: 10 })
      break
    }
    pts.push({ time: t, alertDilation: parseFloat(d.toFixed(2)) })
  }
  return pts
}

function buildActionLine(alertStart: number, maxTime: number): ActionPoint[] {
  const actionStart = alertStart + ACTION_OFFSET_MS
  if (actionStart > maxTime) return []
  const pts: ActionPoint[] = []
  for (let t = actionStart; t <= maxTime; t += LINE_STEP_MS) {
    const d = 4 + CM_PER_MS * (t - actionStart)
    if (d >= 10) {
      pts.push({ time: actionStart + 6 / CM_PER_MS, actionDilation: 10 })
      break
    }
    pts.push({ time: t, actionDilation: parseFloat(d.toFixed(2)) })
  }
  return pts
}

// ── Helpers ────────────────────────────────────────────────────────────────

const truncateNarrativeDisplay = (text: string) => {
  const t = text.trim()
  if (t.length <= NARRATIVE_LABEL_DISPLAY_MAX) return t
  return `${t.slice(0, NARRATIVE_LABEL_DISPLAY_MAX - 1)}…`
}

// ── Cervical Exam Tooltip Helpers ──────────────────────────────────────────

const stationColor = (s: number) =>
  s > 2 ? '#E53E3E' : s > 0 ? '#ED8936' : s < 0 ? '#4299E1' : '#48BB78'

const stationLabel = (s: number) =>
  s <= -3 ? 'Floating' : s <= -1 ? 'High' : s === 0 ? 'At Spines' : s <= 2 ? 'Low' : 'Crowning'

const fmtPosition = (p: CervicalPosition) =>
  p === CervicalPosition.MID_POSITION ? 'Mid' : p.charAt(0).toUpperCase() + p.slice(1)

const fmtConsistency = (c: CervicalConsistency) => c.charAt(0).toUpperCase() + c.slice(1)

const fmtMembranes = (m: MembraneStatus) =>
  m === MembraneStatus.INTACT ? 'Intact' : m.toUpperCase()

const calcBishop = (
  dilation: number,
  effacement: number,
  station: number,
  consistency: CervicalConsistency,
  position: CervicalPosition,
): number => {
  let score = 0
  if (dilation >= 5) score += 3
  else if (dilation >= 3) score += 2
  else if (dilation >= 1) score += 1
  if (effacement >= 80) score += 3
  else if (effacement >= 60) score += 2
  else if (effacement >= 40) score += 1
  if (station >= 1) score += 3
  else if (station >= -1) score += 2
  else if (station >= -2) score += 1
  if (consistency === CervicalConsistency.SOFT) score += 2
  else if (consistency === CervicalConsistency.MEDIUM) score += 1
  if (position === CervicalPosition.ANTERIOR) score += 2
  else if (position === CervicalPosition.MID_POSITION) score += 1
  return score
}

const bishopColor = (score: number) => (score >= 8 ? '#48BB78' : score >= 6 ? '#F6AD55' : '#FC8181')

const bishopLabel = (score: number) =>
  score >= 8 ? 'Favorable' : score >= 6 ? 'Borderline' : 'Unfavorable'

// ── Custom SVG Dots ────────────────────────────────────────────────────────

interface DotRenderProps {
  cx?: number
  cy?: number
  value?: number
}

/** Solid blue circle whose radius scales with the dilation value (0–10 cm) */
const DilationDot = ({ cx = 0, cy = 0, value = 0 }: DotRenderProps) => {
  const r = Math.max(5, Math.min(16, (value / 10) * 16))
  return (
    <circle cx={cx} cy={cy} r={r} fill="rgba(49,130,206,0.18)" stroke="#3182CE" strokeWidth={2} />
  )
}

/** Gold X mark to distinguish fetal station from dilation dots */
const StationDot = ({ cx = 0, cy = 0 }: DotRenderProps) => {
  const s = 7
  return (
    <g>
      <line
        x1={cx - s}
        y1={cy - s}
        x2={cx + s}
        y2={cy + s}
        stroke="#D69E2E"
        strokeWidth={2.5}
        strokeLinecap="round"
      />
      <line
        x1={cx + s}
        y1={cy - s}
        x2={cx - s}
        y2={cy + s}
        stroke="#D69E2E"
        strokeWidth={2.5}
        strokeLinecap="round"
      />
    </g>
  )
}

// ── Shared Cervical Exam Display ────────────────────────────────────────────

export interface CervicalExamDisplayProps {
  dilation: number
  effacement: number
  station: number
  position: CervicalPosition
  consistency: CervicalConsistency
  membranes: MembraneStatus
  presentingPart: string
}

/** Full cervical exam summary — used in both the partogram tooltip and the entry click popover. */
export const CervicalExamDisplay = ({
  dilation,
  effacement,
  station,
  position,
  consistency,
  membranes,
  presentingPart,
}: CervicalExamDisplayProps) => {
  const sColor = stationColor(station)
  const sLabel = stationLabel(station)
  const bishop = calcBishop(dilation, effacement, station, consistency, position)

  return (
    <Flex direction="column" gap={0.5}>
      {/* Primary measurements */}
      <Flex justify="space-between" align="center">
        <Text fontSize="xs" color="gray.400">
          Dilation
        </Text>
        <Text fontSize="sm" fontWeight={600} color="#3182CE">
          {dilation} cm
        </Text>
      </Flex>
      <Flex justify="space-between" align="center">
        <Text fontSize="xs" color="gray.400">
          Effacement
        </Text>
        <Text fontSize="sm" fontWeight={600} color="#276749" _dark={{ color: '#68D391' }}>
          {effacement}%
        </Text>
      </Flex>
      <Flex justify="space-between" align="center">
        <Text fontSize="xs" color="gray.400">
          Station
        </Text>
        <Flex align="baseline" gap={1.5}>
          <Text fontSize="sm" fontWeight={600} color={sColor}>
            {station > 0 ? `+${station}` : station}
          </Text>
          <Text fontSize="xs" color={sColor} opacity={0.8}>
            {sLabel}
          </Text>
        </Flex>
      </Flex>

      {/* Cervical assessment */}
      <Divider my={1} />
      <Flex justify="space-between" align="center">
        <Text fontSize="xs" color="gray.400">
          Position
        </Text>
        <Text fontSize="xs" color="gray.600" _dark={{ color: 'gray.300' }}>
          {fmtPosition(position)}
        </Text>
      </Flex>
      <Flex justify="space-between" align="center">
        <Text fontSize="xs" color="gray.400">
          Consistency
        </Text>
        <Text fontSize="xs" color="gray.600" _dark={{ color: 'gray.300' }}>
          {fmtConsistency(consistency)}
        </Text>
      </Flex>
      <Flex justify="space-between" align="center">
        <Text fontSize="xs" color="gray.400">
          Membranes
        </Text>
        <Text fontSize="xs" color="gray.600" _dark={{ color: 'gray.300' }}>
          {fmtMembranes(membranes)}
        </Text>
      </Flex>
      {presentingPart ? (
        <Flex justify="space-between" align="center">
          <Text fontSize="xs" color="gray.400">
            Presenting
          </Text>
          <Text fontSize="xs" color="gray.600" _dark={{ color: 'gray.300' }}>
            {presentingPart}
          </Text>
        </Flex>
      ) : null}

      {/* Bishop score */}
      <Divider my={1} />
      <Flex justify="space-between" align="center" mb={0.5}>
        <Text
          fontSize="xs"
          fontWeight={600}
          letterSpacing="wider"
          color="gray.400"
          _dark={{ color: 'gray.500' }}>
          BISHOP
        </Text>
        <Flex align="baseline" gap={1}>
          <Text fontSize="sm" fontWeight={700} color={bishopColor(bishop)}>
            {bishop}
          </Text>
          <Text fontSize="xs" color="gray.400">
            / 13
          </Text>
          <Text fontSize="xs" fontWeight={600} color={bishopColor(bishop)} ml={0.5}>
            {bishopLabel(bishop)}
          </Text>
        </Flex>
      </Flex>
      <Box
        h="4px"
        w="100%"
        borderRadius="full"
        bg="gray.100"
        _dark={{ bg: 'gray.700' }}
        overflow="hidden">
        <Box h="100%" borderRadius="full" bg={bishopColor(bishop)} w={`${(bishop / 13) * 100}%`} />
      </Box>
    </Flex>
  )
}

// ── Custom Tooltip ─────────────────────────────────────────────────────────

interface TooltipEntry {
  dataKey: string
  value: number | null | undefined
  payload: PartogramDataPoint
}

/**
 * `narrativeMarkers` is threaded in via a render-function closure on the
 * `content` prop so that we can fall back to a label-based lookup when
 * recharts filters null-valued payload entries for the narrative-only points.
 */
interface PartogramTooltipProps {
  active?: boolean
  payload?: TooltipEntry[]
  /** Raw XAxis value at the cursor position (numeric time in ms) */
  label?: number | string
  narrativeMarkers: NarrativeMarker[]
}

const safeFormatTime = (ms: number) => {
  try {
    return format(new Date(ms), 'HH:mm')
  } catch {
    return ''
  }
}

const PartogramTooltip = ({ active, payload, label, narrativeMarkers }: PartogramTooltipProps) => {
  if (!active) return null

  const pt: PartogramDataPoint | undefined = (
    payload?.find(p => 'dilation' in (p.payload ?? {})) ?? payload?.[0]
  )?.payload
  const { dilation, station, effacement, position, consistency, membranes, presentingPart } =
    pt ?? {}
  const time = pt?.time ?? (typeof label === 'number' ? label : Number(label))
  const noteBody = pt?.noteBody ?? narrativeMarkers.find(n => n.time === Number(label))?.label

  const hasFullCervical =
    dilation != null &&
    effacement != null &&
    station != null &&
    position != null &&
    consistency != null &&
    membranes != null

  const hasData = dilation != null || station != null || noteBody
  if (!hasData) return null

  return (
    <Box
      bg="white"
      _dark={{ bg: 'gray.800', borderColor: 'whiteAlpha.300' }}
      border="1px solid"
      borderColor="gray.200"
      borderRadius={8}
      px={3}
      py={2.5}
      shadow="md"
      fontFamily="hero-new"
      fontSize="sm"
      minW="200px"
      maxW="280px">
      {time != null ? (
        <Text fontWeight={600} color="gray.500" _dark={{ color: 'gray.400' }} mb={1.5}>
          {safeFormatTime(time)}
        </Text>
      ) : null}

      {hasFullCervical ? (
        <>
          <Divider mb={1.5} />
          <CervicalExamDisplay
            dilation={dilation}
            effacement={effacement}
            station={station}
            position={position}
            consistency={consistency}
            membranes={membranes}
            presentingPart={presentingPart ?? ''}
          />
        </>
      ) : dilation != null || station != null ? (
        <>
          <Divider mb={1.5} />
          <Flex direction="column" gap={0.5}>
            {dilation != null ? (
              <Flex justify="space-between" align="center">
                <Text fontSize="xs" color="gray.400">
                  Dilation
                </Text>
                <Text fontSize="sm" fontWeight={600} color="#3182CE">
                  {dilation} cm
                </Text>
              </Flex>
            ) : null}
            {station != null ? (
              <Flex justify="space-between" align="center">
                <Text fontSize="xs" color="gray.400">
                  Station
                </Text>
                <Text fontSize="sm" fontWeight={600} color="#D69E2E">
                  {station > 0 ? `+${station}` : station}
                </Text>
              </Flex>
            ) : null}
          </Flex>
        </>
      ) : null}

      {noteBody ? (
        <>
          <Box h="1px" bg="gray.100" _dark={{ bg: 'whiteAlpha.200' }} my={1.5} />
          <Text
            fontSize="xs"
            fontWeight={600}
            letterSpacing="wider"
            color="gray.400"
            _dark={{ color: 'gray.500' }}
            mb={0.5}>
            NOTE
          </Text>
          <Text color="gray.700" _dark={{ color: 'gray.200' }} whiteSpace="pre-wrap" fontSize="xs">
            {noteBody}
          </Text>
        </>
      ) : null}
    </Box>
  )
}

/**
 * ReferenceLine label: truncated text on the chart; Chakra Tooltip with full note on hover.
 * Uses foreignObject so the trigger lives in HTML while the chart is SVG.
 */
const NarrativeMarkerLabel = ({
  viewBox,
  position = 'top',
  offset = 5,
  fullLabel,
}: Pick<LabelProps, 'viewBox' | 'position' | 'offset'> & {
  fullLabel: string
}) => {
  if (!viewBox || 'cx' in viewBox) return null
  const cartesian = viewBox as {
    x: number
    y: number
    width: number
    height: number
    upperWidth?: number
  }
  const { x, y, width, height } = cartesian
  const upperW = cartesian.upperWidth ?? width
  const verticalSign = height >= 0 ? 1 : -1
  const offsetNum = typeof offset === 'number' ? offset : 5
  const verticalOffset = verticalSign * offsetNum
  const cx = x + upperW / 2
  const labelY = position === 'top' ? y - verticalOffset : y + height + verticalOffset
  const display = truncateNarrativeDisplay(fullLabel)

  const foX = cx - NARRATIVE_LABEL_FOREIGN_OBJECT_W / 2
  const foY = labelY - NARRATIVE_LABEL_FOREIGN_OBJECT_H + 3

  return (
    <g className="recharts-narrative-marker-label">
      <foreignObject
        x={foX}
        y={foY}
        width={NARRATIVE_LABEL_FOREIGN_OBJECT_W}
        height={NARRATIVE_LABEL_FOREIGN_OBJECT_H}
        style={{ overflow: 'visible' }}>
        <div
          {...({
            xmlns: 'http://www.w3.org/1999/xhtml',
            style: {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
            },
          } as HTMLAttributes<HTMLDivElement> & { xmlns: string })}>
          <Text
            fontSize="8px"
            lineHeight="1.2"
            fontFamily="hero-new"
            color="gray.500"
            _dark={{ color: 'gray.400' }}
            cursor="default"
            textAlign="center"
            noOfLines={1}
            maxW={`${NARRATIVE_LABEL_FOREIGN_OBJECT_W}px`}>
            {display}
          </Text>
        </div>
      </foreignObject>
    </g>
  )
}

export type LaborChartXAxisProps = ComponentProps<typeof XAxis>

export const DilationStationGraph: FC<{
  partogramData: PartogramDataPoint[]
  narrativeMarkers: NarrativeMarker[]
  procedureOverlays: ProcedureOverlay[]
  xDomain: [number, number]
  xAxisProps: LaborChartXAxisProps
  gridColor: string
  tickStyle: { fontFamily: string; fontSize: number; fill: string }
  onChartClick: (data: any, event: MouseEvent) => void
  onProcedureClick: (e: MouseEvent, entryId: string) => void
}> = ({
  partogramData,
  narrativeMarkers,
  procedureOverlays,
  xDomain,
  xAxisProps,
  gridColor,
  tickStyle,
  onChartClick,
  onProcedureClick,
}) => {
  const alertLineData = useMemo<AlertPoint[]>(() => {
    const [, maxTime] = xDomain
    const onset = partogramData.find(p => p.dilation != null && p.dilation >= 4)
    if (!onset) return []
    return buildAlertLine(onset.time, maxTime)
  }, [partogramData, xDomain])

  const actionLineData = useMemo<ActionPoint[]>(() => {
    const [, maxTime] = xDomain
    const onset = partogramData.find(p => p.dilation != null && p.dilation >= 4)
    if (!onset) return []
    return buildActionLine(onset.time, maxTime)
  }, [partogramData, xDomain])

  const dilationLegendItems = useMemo((): ChartLegendItem[] => {
    const items: ChartLegendItem[] = [
      { kind: 'line', label: 'Cervical Dilation', color: '#3182CE' },
      { kind: 'line', label: 'Fetal Station', color: '#D69E2E', dashed: true },
    ]
    if (alertLineData.length > 0) {
      items.push({
        kind: 'line',
        label: 'Alert Line',
        color: '#FC8181',
        dashed: true,
      })
    }
    if (actionLineData.length > 0) {
      items.push({
        kind: 'line',
        label: 'Action Line',
        color: '#F6AD55',
        dashed: true,
      })
    }
    if (procedureOverlays.some(p => p.endTime != null)) {
      items.push({
        kind: 'band',
        label: 'Procedure (duration)',
        fill: 'rgba(154,230,180,0.18)',
      })
    }
    if (procedureOverlays.some(p => p.endTime == null)) {
      items.push({
        kind: 'line',
        label: 'Procedure (event)',
        color: '#9F7AEA',
        dashed: true,
      })
    }
    if (narrativeMarkers.length > 0) {
      items.push({
        kind: 'line',
        label: 'Narrative note',
        color: 'rgba(160,174,192,0.85)',
        dashed: true,
      })
    }
    return items
  }, [alertLineData.length, actionLineData.length, procedureOverlays, narrativeMarkers.length])

  return (
    <>
      <Box w="100%" h="340px" pl={1} pr={1} cursor="pointer">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            margin={{
              top:
                narrativeMarkers.length > 0
                  ? PARTOGRAM_MARGIN_TOP_WITH_NARRATIVES
                  : PARTOGRAM_MARGIN_TOP_DEFAULT,
              right: 8,
              left: 8,
              bottom: 8,
            }}
            syncId="labor"
            onClick={onChartClick}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />

            <XAxis {...xAxisProps} />

            <YAxis
              yAxisId="dilation"
              orientation="left"
              domain={[0, 10]}
              ticks={[0, 2, 4, 6, 8, 10]}
              tick={tickStyle}
              label={{
                value: 'Dilation (cm)',
                angle: -90,
                position: 'middle',
                style: {
                  fontFamily: 'hero-new',
                  fontSize: 14,
                  fill: '#3182CE',
                },
              }}
            />

            <YAxis
              yAxisId="station"
              orientation="right"
              domain={[-5, 5]}
              ticks={[-5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5]}
              reversed
              tick={tickStyle}
              tickFormatter={v => (v > 0 ? `+${v}` : String(v))}
              label={{
                value: 'Station',
                angle: 90,
                position: 'middle',
                style: {
                  fontFamily: 'hero-new',
                  fontSize: 16,
                  fill: '#D69E2E',
                },
              }}
            />

            <RechartsTooltip
              isAnimationActive={false}
              content={props => (
                <PartogramTooltip
                  active={props.active}
                  payload={props.payload as unknown as TooltipEntry[] | undefined}
                  label={props.label}
                  narrativeMarkers={narrativeMarkers}
                />
              )}
            />

            {procedureOverlays
              .filter(p => p.endTime != null)
              .map((proc, i) => (
                <ReferenceArea
                  key={`proc-area-${i}`}
                  yAxisId="dilation"
                  x1={proc.startTime}
                  x2={proc.endTime}
                  fill="rgba(154,230,180,0.18)"
                  onClick={e => {
                    e.stopPropagation()
                    onProcedureClick(e, proc.entryId)
                  }}
                  stroke="#48BB78"
                  strokeDasharray="4 4"
                  label={{
                    value: proc.name,
                    position: 'insideTop',
                    style: {
                      fontFamily: 'hero-new',
                      fontSize: 12,
                      fill: '#276749',
                    },
                  }}
                />
              ))}

            {procedureOverlays
              .filter(p => p.endTime == null)
              .map((proc, i) => (
                <ReferenceLine
                  key={`proc-line-${i}`}
                  yAxisId="dilation"
                  x={proc.startTime}
                  stroke="#9F7AEA"
                  strokeWidth={1.5}
                  strokeDasharray="5 4"
                  label={{
                    value: proc.name,
                    position: 'insideTopRight',
                    style: {
                      fontFamily: 'hero-new',
                      fontSize: 12,
                      fill: '#6B46C1',
                    },
                  }}
                />
              ))}

            {narrativeMarkers.map((n, i) => (
              <ReferenceLine
                key={`narr-${i}`}
                yAxisId="dilation"
                x={n.time}
                stroke="rgba(160,174,192,0.6)"
                strokeDasharray="2 5"
                label={{
                  position: 'top',
                  content: (labelProps: LabelProps) => (
                    <NarrativeMarkerLabel
                      viewBox={labelProps.viewBox}
                      position={labelProps.position}
                      offset={labelProps.offset}
                      fullLabel={n.label}
                    />
                  ),
                }}
              />
            ))}

            {alertLineData.length > 0 && (
              <Line
                yAxisId="dilation"
                data={alertLineData}
                dataKey="alertDilation"
                name="Alert Line"
                stroke="#FC8181"
                strokeWidth={1.5}
                strokeDasharray="7 4"
                dot={false}
                activeDot={false}
                isAnimationActive={false}
              />
            )}

            {actionLineData.length > 0 && (
              <Line
                yAxisId="dilation"
                data={actionLineData}
                dataKey="actionDilation"
                name="Action Line"
                stroke="#F6AD55"
                strokeWidth={1.5}
                strokeDasharray="7 4"
                dot={false}
                activeDot={false}
                isAnimationActive={false}
              />
            )}

            <Line
              yAxisId="dilation"
              data={partogramData}
              dataKey="dilation"
              name="Dilation"
              stroke="#3182CE"
              strokeWidth={2}
              connectNulls={true}
              dot={(props: any) =>
                props.value != null ? (
                  <DilationDot cx={props.cx} cy={props.cy} value={props.value} />
                ) : (
                  <g />
                )
              }
              activeDot={(props: any) =>
                props.value != null ? (
                  <circle
                    cx={props.cx}
                    cy={props.cy}
                    r={7}
                    stroke="#3182CE"
                    strokeWidth={2}
                    fill="white"
                  />
                ) : (
                  <g />
                )
              }
              isAnimationActive={false}
            />

            <Line
              yAxisId="dilation"
              data={partogramData}
              dataKey="_snap"
              stroke="none"
              strokeWidth={0}
              dot={false}
              activeDot={false}
              isAnimationActive={false}
              legendType="none"
            />

            <Line
              yAxisId="station"
              data={partogramData}
              dataKey="station"
              name="Station"
              stroke="#D69E2E"
              strokeWidth={2}
              strokeDasharray="4 3"
              connectNulls={true}
              dot={(props: any) =>
                props.value != null ? <StationDot cx={props.cx} cy={props.cy} /> : <g />
              }
              activeDot={(props: any) =>
                props.value != null ? (
                  <circle
                    cx={props.cx}
                    cy={props.cy}
                    r={7}
                    stroke="#D69E2E"
                    strokeWidth={2}
                    fill="white"
                  />
                ) : (
                  <g />
                )
              }
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
      <ChartLegend items={dilationLegendItems} />
    </>
  )
}
